/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { StoryLoading } from "../basic/story-loading/story-loading";
import get from "lodash/get";
import { RequestMeteredSubsciption } from "../basic/request-subscription/request-metered-subscsription";
import { RequestSubscription } from "../basic/request-subscription/request-subscription";
import { StoryPageCard } from "../story-page-components/story-page-card";
import { AdService } from "../ad-service";
import React from "react";
import { getOverride, selectedStoryTemplate, accessCheck, getInfiniteScrollConfigStyle } from "../../utils";

async function getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config) {
  const enableAccessTypePb = get(config, [
    "pagebuilder-config",
    "general",
    "accesstypeIntegration",
    "enableAccesstype",
  ]);
  const enableAccessTypeAhead = get(config, ["theme-attributes", "enableAccesstype"], false);
  const enableAccessType = enableAccessTypePb !== undefined ? enableAccessTypePb : enableAccessTypeAhead;
  const storyId = get(story, ["id"]);
  let access = {};

  if (accessCheck(enableAccessType) && !checkIfStoryPublic(story)) {
    access = await checkAccess(storyId);
  } else {
    access = generateSubscribedAccess(storyId);
    const accessById = { [storyId]: access };
    if (typeof accessIsLoading === "function" && typeof accessUpdated === "function") {
      accessIsLoading(false);
      accessUpdated(accessById);
    }
  }

  return access;
}

function generateSubscribedAccess(storyId) {
  return { granted: true, grantReason: "SUBSCRIBER", data: {} };
}

const getTruncatedCard = (storyCards = []) => {
  const firstElement = get(storyCards, ["0", "story-elements", "0"], {});
  if (firstElement.text) {
    const content = get(firstElement, ["text"], "");
    const metadataContent = get(firstElement, ["metadata", "content"], "");

    const truncatedCard = [
      {
        "story-elements": [
          {
            ...firstElement,
            metadata: { content: metadataContent.slice(0, 200) + "..." },
            text: content.slice(0, 200) + "...",
          },
        ],
      },
    ];
    return truncatedCard;
  }
  return [{ "story-elements": [] }];
};

function getIfAccessLoading(storyCards = [], story, cdnImage, getStoryCardTemplate, config, firstStory) {
  const storyTemplate = selectedStoryTemplate("text-story", story, config);
  return (
    <div className={`card-background-color ${getTemplate(storyTemplate)}`}>
      {getStoryCards(getTruncatedCard(storyCards), config, story, cdnImage, getStoryCardTemplate, firstStory)}
      <StoryLoading />
    </div>
  );
}

function getIfHasAccess(storyCards, config, story, cdnImage, getStoryCardTemplate, firstStory) {
  return getStoryCards(storyCards, config, story, cdnImage, getStoryCardTemplate, firstStory);
}

function getIfMeteredAccess(storyCards, access, story, cdnImage, getStoryCardTemplate, config, firstStory) {
  const accessData = get(access, ["data"], {});
  const pbAccessTypeConfig = get(config, ["pagebuilder-config", "general", "accesstypeIntegration"]);
  const disableMetering = get(
    pbAccessTypeConfig,
    ["disableMetering"],
    get(config, ["theme-attributes", "disableMetering"]) === "true"
  );

  if (!disableMetering || accessData.numberRemaining > -1) {
    return getStoryCards(storyCards, config, story, cdnImage, getStoryCardTemplate, firstStory);
  }

  return (
    <div>
      {getStoryCards(getTruncatedCard(storyCards), story, cdnImage, getStoryCardTemplate, config, firstStory)}
      <RequestMeteredSubsciption subscriptionCount={accessData.numberRemaining} />
    </div>
  );
}

function getIfNoAccess(storyCards, story, cdnImage, getStoryCardTemplate, config, firstStory) {
  const storyTemplate = selectedStoryTemplate("text-story", story, config);
  return (
    <>
      {getStoryCards(getTruncatedCard(storyCards), config, story, cdnImage, getStoryCardTemplate, firstStory)}
      <div className={`card-background-color ${getTemplate(storyTemplate)}`}>
        <RequestSubscription config={config} story={story} />
      </div>
    </>
  );
}

function getTemplate(storyTemplate) {
  switch (storyTemplate) {
    case "hero-priority":
      return "grid-4-12";
    case "hero-overlay":
      return "grid-4-12";
    case "headline-hero-priority":
      return "grid-4-12";
    case "headline-sideway":
      return "grid-4-12";
    case "headline-priority":
      return "grid-3-10";
    default:
      return "grid-2-10";
  }
}

const getBulletStyle = new Proxy(
  {
    bullets: "listicle bullets",
    321: "listicle reverse",
  },
  getOverride("listicle")
);

function getStoryCards(cards = [], config, story, cdnImage, getStoryCardTemplate, firstStory) {
  if (typeof getStoryCardTemplate === "function") {
    return getStoryCardTemplate(cards);
  }

  const hasMultiCards = cards.length > 1;
  const textStoryTemplate = selectedStoryTemplate("text-story", story, config);
  const listicleStoryTemplate = selectedStoryTemplate("listicle-story", story, config);
  const template = get(story, ["story-template"]);
  const storyTemplate = template === "listicle" ? listicleStoryTemplate : textStoryTemplate;
  const bulletType = get(story, ["bullet-type"]);
  const firstStoryCard = get(story, ["cards", 0]);
  const infiniteScrollStyle = getInfiniteScrollConfigStyle(config);

  if (infiniteScrollStyle === "read-more" && !firstStory) {
    return (
      <div className={`card-background-color`}>
        <StoryPageCard
          key={firstStoryCard.id}
          config={config}
          card={firstStoryCard}
          story={story}
          cdn={cdnImage}
          clazzName="grid-column-full"
          firstStory={firstStory}
        />
      </div>
    );
  } else {
    return cards.map((card, index) => {
      if (card["card-type"] === "introduction" && index === 0) {
        return (
          <div key={card.index} className={`card-background-color ${getTemplate(storyTemplate)}`}>
            <StoryPageCard
              key={card.id}
              config={config}
              card={card}
              story={story}
              cdn={cdnImage}
              clazzName="grid-column-full"
              firstStory={firstStory}
            />
            <div className="app-ad app-ad--story-horizontal grid-column-full">
              <AdService adtype="Story-Middle-Ad" slotId="Story-Middle-Horizontal" />
            </div>
          </div>
        );
      } else if (index === 0 && hasMultiCards) {
        return (
          <div className={`card-background-color get-card ${getBulletStyle[bulletType]} ${getTemplate(storyTemplate)}`}>
            <StoryPageCard
              key={card.id}
              card={card}
              config={config}
              story={story}
              cdn={cdnImage}
              clazzName="grid-column-full"
              firstStory={firstStory}
            />
            <div className="app-ad app-ad--story-horizontal grid-column-full">
              <AdService adtype="Story-Middle-Ad" slotId="Story-Middle-Horizontal" />
            </div>
          </div>
        );
      } else {
        return (
          <div className={`card-background-color ${getBulletStyle[bulletType]} ${getTemplate(storyTemplate)}`}>
            <StoryPageCard
              key={card.id}
              card={card}
              config={config}
              story={story}
              cdn={cdnImage}
              clazzName="grid-column-full"
              firstStory={firstStory}
            />
          </div>
        );
      }
    });
  }
}

function handleAccess(story, storyAccess, accessLoading, config, storyCardTemplate, firstStory) {
  const storyId = get(story, ["id"]);
  const access = get(storyAccess, [storyId]);
  const granted = get(access, ["granted"], false);
  const storyCards = get(story, ["cards"], []);
  const cdnImage = get(config, ["cdn-image"], "");

  if (accessLoading) {
    return getIfAccessLoading(storyCards, story, cdnImage, storyCardTemplate, config, firstStory);
  }

  if (granted && access && !accessLoading) {
    const grantReason = get(access, ["grantReason"]);

    switch (grantReason) {
      case "METERING":
        return getIfMeteredAccess(storyCards, access, story, cdnImage, storyCardTemplate, config, firstStory);
      case "SUBSCRIBER":
        return getIfHasAccess(storyCards, config, story, cdnImage, storyCardTemplate, firstStory);
    }
  }

  return getIfNoAccess(storyCards, story, cdnImage, storyCardTemplate, config, firstStory);
}

function checkIfStoryPublic(story) {
  const access = get(story, ["access"]);
  return access === "public" || access === null;
}

const navigateToAuthDomain = async (bridgeKeeperClientId="") => {
  if (window && window.location) {
    const { getOauthAuthorizeUrl } = await import("@quintype/bridgekeeper-js");
    const redirectUrl = `${window.location.origin}/api/auth/v1/oauth/token`;
    const oauthAuthorizeUrl = await getOauthAuthorizeUrl(
      bridgeKeeperClientId,
      redirectUrl,
      encodeURIComponent(window.location.href)
    );
    window.location.replace(oauthAuthorizeUrl);
  }
};

const checkIfSingleSignOn = (sso = {}) => {
  const { enableSingleSignOn = "", bridgeKeeperClientId="" } = sso;
  return bridgeKeeperClientId && enableSingleSignOn;
};

const getLoginLink =(member,sso,bridgeKeeperClientId, openLogin, event) => {
  return ( !member || !member["verification-status"]) && checkIfSingleSignOn(sso) ? navigateToAuthDomain(bridgeKeeperClientId) : openLogin(event);
}

export {
  getAccessData,
  getIfAccessLoading,
  getIfHasAccess,
  getIfMeteredAccess,
  getIfNoAccess,
  getStoryCards,
  handleAccess,
  checkIfStoryPublic,
  navigateToAuthDomain,
  checkIfSingleSignOn,
  getLoginLink,
};
